// StoreInfo.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
//import { styled } from '@mui/material/styles';
import { CircularProgress, Dialog, IconButton } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite'; // 사용
import StopCircleIcon from '@mui/icons-material/StopCircle'; // 사용중지.
import PageviewIcon from '@mui/icons-material/Pageview';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {ContentConainer, ContentHeader, ContentBody, ControlBox, ToolBox} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import FlexyTable from '../../common/flexytable/FlexyTable';
import DialogHeader from '../../common/DialogHeader';
import VzmSelector from '../../common/VzmSelector';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, storeInfoListRepo, storeTypeListRepo } from '../../value/AppModel';
import {
    LIST_STORE_INFO, GET_STORE_INFO,
    ADD_STORE_INFO, EDIT_STORE_INFO, REMOVE_STORE_INFO, SET_USAGE_STORE_INFO
} from './StoreInfoGql';
import { StoreInfoDictionary } from './StoreInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import StoreInfoView from './StoreInfoView';
import StoreInfoInput from './StoreInfoInput';
import { LIST_STORE_TYPE } from '../store_type/StoreTypeGql';

const IgnoreStoreType = {storeTypeCd:'__**__', storeTypeName:AppWord.AllKorean};
/**
 * (STORE_INFO) 정보 View Controller
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-09-03 09:47:09 using DbdbTap
 */
export default function StoreInfo({
    open,
    onClose,
}) {
    const [viewerState, setViewerState] = useState(AppObject.EmptyViewerState);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const storeInfoListData = useReactiveVar(storeInfoListRepo);
    const storeTypeListData = useReactiveVar(storeTypeListRepo);
    const [byStoreType, setByStoreType] = useState(IgnoreStoreType.storeTypeCd); // storeTypeCd. if null, no filter.
    const [storeTypeSelected, setStoreTypeSelected] = useState(null);
    const [searchText, setSearchText] = useState('');
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const openRef = useRef(0);
    const lastReloadRef = useRef(0);
    const isEditingRef = useRef(false); // 플래그 생성

    // ##### Call GraphQL to get List #####
    const [getStoreTypeList, responseStoreTypeList] = useLazyQuery(LIST_STORE_TYPE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetTypeList(data, option)},
		onError: (error) => addAlert({title: '점포구분코드 목록 조회 오류', message: [error.message]})
    });
    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    const [getStoreInfoList, responseList] = useLazyQuery(LIST_STORE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: StoreInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getStoreInfoItem, responseItem] = useLazyQuery(GET_STORE_INFO, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: StoreInfoDictionary.errorTitle.Get, message: [error.message]})
	});
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addStoreInfo, responseAdd] = useMutation( ADD_STORE_INFO, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: StoreInfoDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editStoreInfo, responseEdit] = useMutation( EDIT_STORE_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: StoreInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeStoreInfo, responseRemove] = useMutation( REMOVE_STORE_INFO, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: StoreInfoDictionary.errorTitle.Remove, message: [error.message]})
	});
    const [setUsageStoreInfo, responseSetUsage] = useMutation( SET_USAGE_STORE_INFO, {
        onCompleted: (data, option) => onComplateSetUsage(data, option),
        onError: (error) => addAlert({title: StoreInfoDictionary.errorTitle.SetUsage, message: [error.message]})
    });

    const getStoreInfoListNow = () => {
        getStoreInfoList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    const getStoreTypeListNow = () => {
        getStoreTypeList();
    };

    useEffect(() => {
        if(open) {
            if(openRef.current === 0) {
                let diff = new Date().getTime()/1000 - storeInfoListData.when;
                if(diff > 300)
                    getStoreInfoListNow();
                diff = new Date().getTime()/1000 - storeTypeListData.when;
                if(diff > 300)
                    getStoreTypeListNow();
            }
            openRef.current++;
        }
        else {
            openRef.current = 0;
        }
    }, [open]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetTypeList = (data, clientOption) => {
        if(data.storeTypeList) {
            storeTypeListRepo({when:new Date().getTime()/1000, records:data.storeTypeList});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.storeInfoList) {
            storeInfoListRepo({when:new Date().getTime()/1000, records:data.storeInfoList});
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.storeInfoItem) {
            if(isEditingRef.current) setEditorState({open:true, item:data.storeInfoItem});
            else setViewerState({open:true, item:data.storeInfoItem});
        }
        else addAlert({title:StoreInfoDictionary.errorTitle.Get, message:['점포정보를 찾을 수 없습니다.']});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.storeInfoAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getStoreInfoListNow();
        }
        else addAlert({title:StoreInfoDictionary.errorTitle.Add, message:[data.storeInfoAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.storeInfoEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getStoreInfoListNow();
        }
        else addAlert({title:StoreInfoDictionary.errorTitle.Edit, message:[data.storeInfoEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.storeInfoRemove.ok) getStoreInfoListNow();
        else addAlert({title:StoreInfoDictionary.errorTitle.Remove, message:[data.storeInfoRemove.message]});
    };

    const onComplateSetUsage = (data, option) => {
        if(data.storeInfoSetUsage.ok) getStoreInfoListNow();
        else addAlert({title:StoreInfoDictionary.errorTitle.SetUsage, message:[data.storeInfoSetUsage.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getStoreInfoListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{storeInfo:item}};
        if(isEdit) editStoreInfo(mutateParam);
        else addStoreInfo(mutateParam);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        isEditingRef.current = true;
        getStoreInfoItem({variables:{storeId:item.storeId}});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "점포정보 삭제",
            content: [
                `${item.storeName} 점포를 삭제하시겠습니까?`,
                '동적점포 설정, 도착지 설정 등에서 사용되고 있는 경우에는 삭제하지 못할 수 있습니다.',
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeStoreInfo({variables:{storeInfo:{storeId:item.storeId}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onDoubleClickRow = (record) => {
        isEditingRef.current = false;
        getStoreInfoItem({variables:{storeId:record.storeId}});
    };

    const onCloseView = () => {
        setViewerState(AppObject.EmptyViewerState);
    };

    const onRequestEditFromViewer = (item) => {
        setViewerState(AppObject.EmptyViewerState);
        setEditorState({open: true, item: item});
    };

    const confirmStopUsing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "점포 사용중지",
            content: [
                `${item.storeName} 점포의 사용을 중지하시겠습니까?`,
            ]
        });

        if(confirm)
            setUsageStoreInfo({variables:{storeInfo:{storeId:item.storeId, useYn:'N'}}});
    };

    const confirmReuse = async (item) => {
        const confirm = await openConfirmDialog({
            title: "점포 재사용",
            content: [
                `${item.storeName} 점포의 사용을 재개하시겠습니까?`,
            ]
        });

        if(confirm)
            setUsageStoreInfo({variables:{storeInfo:{storeId:item.storeId, useYn:'Y'}}});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            case AppObject.command.STOP_USING: confirmStopUsing(row); break;
            case AppObject.command.REUSE: confirmReuse(row); break;
            case AppObject.command.VIEW: onDoubleClickRow(row); break;
            default: return;
        }
    };

    /*
	CALL wps_set_auth_table('pr_store_info_add', '1,3', v_msg);
	CALL wps_set_auth_table('pr_store_info_edit', '1,3', v_msg);
	CALL wps_set_auth_table('pr_store_info_remove', '1,3', v_msg);
	CALL wps_set_auth_table('pr_store_info_set_usage', '1,3', v_msg);
	CALL wps_set_auth_table('pr_store_info_set_zone', '1,3', v_msg);
    */

    const canAdd = checkAuth(authTableTree.tree.pr_store_info_add);
    const canEdit = checkAuth(authTableTree.tree.pr_store_info_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_store_info_remove);
    const canSetUsage = checkAuth(authTableTree.tree.pr_store_info_set_usage);
    
    const tools = [
        {id:AppObject.command.VIEW, icon:<PageviewIcon/> }
    ];

    if(canEdit) tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});
    if(canSetUsage) {
        tools.push(
            {id:AppObject.command.STOP_USING, icon:<StopCircleIcon/>, color: AppStyle.mui.error, where:(row)=>row.useYn === "Y"},
            {id:AppObject.command.REUSE, icon:<PlayCircleFilledWhiteIcon/>, color: AppStyle.mui.success, where:(row)=>row.useYn !== "Y"}
        );
    }
    if(canRemove)
        tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    const loadingAny
    = responseItem.loading
    || responseAdd.loading
    || responseEdit.loading
    || responseRemove.loading
    || responseList.loading
    || responseSetUsage.loading
    || responseStoreTypeList.loading;

    const renderCore = () => {
        return(
            <ContentConainer>
                <ContentHeader>
                    <ControlBox>
                        <Box width={220} style={{marginRight:20}}>
                            <VzmSelector
                                value={byStoreType}
                                dict={{
                                    id: 'storeTypeCd',
                                    valueKey: 'storeTypeCd',
                                    labelKey: 'storeTypeName',
                                }}
                                onChange={(id,value)=>setByStoreType(value)}
                                onSelectRecord={(record)=>setStoreTypeSelected(record)}
                                list={[IgnoreStoreType].concat(storeTypeListData.records)}
                                fullWidth={true}
                            />
                        </Box>
                        <SearchBox
                            value={searchText}
                            onChangeText={onChangeSearchText}
                        />
                    </ControlBox>
                    <ToolBox>
                        {
                            loadingAny ?
                            <CircularProgress size={24} />
                            :
                            <IconButton
                                color={AppStyle.mui.inherit}
                                onClick={onClickReload}
                            >
                                <CachedIcon />
                            </IconButton>}
                        {
                            canAdd &&
                            <IconButton
                                color={AppStyle.mui.primary}
                                style={{marginLeft:10}}
                                onClick={onClickAdd}
                            >
                                <NoteAddIcon />
                            </IconButton>
                        }
                    </ToolBox>
                </ContentHeader>
                <FlexyTable
                    name={StoreInfoDictionary.tableProps.tableId}
                    tableProps={StoreInfoDictionary.tableProps}
                    records={
                        storeInfoListData.records.filter((item)=>{
                            if(byStoreType && byStoreType !== IgnoreStoreType.storeTypeCd)
                                return item.storeTypeCd === byStoreType;
                            else return true;
                        })
                    }
                    search={searchText}
                    tools={tools}
                    onClickTool={onClickToolOnRow}
                    onRowDoubleClicked={onDoubleClickRow}
                />
                <StoreInfoView
                    open={viewerState.open}
                    item={viewerState.item}
                    onRequestEdit={onRequestEditFromViewer}
                    onClose={onCloseView}
                />
                <StoreInfoInput
                    open={editorState.open}
                    item={editorState.item}
                    onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                    responseSaving={editorState.item ? responseEdit : responseAdd}
                    onRequestSave={onRequestSave}
                    defaultStoreType={storeTypeSelected}
                />
            </ContentConainer>
        );
    }

    return (
        <Dialog open={open} fullScreen>
            <DialogHeader
                title="점포 관리"
                onClose={onClose}
            />
            <ContentBody style={{position:'relative'}}>
                <Box style={{position:'absolute', top:0,left:0,bottom:0,right:0}}>
                    {renderCore()}
                </Box>
            </ContentBody>
        </Dialog>
    );
}
